import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import apiService from '../services/apiService';
import '../styles/EditCoordinatesModal.css';

const EditCoordinatesModal = ({ order, onClose, onSuccess, allowManual = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(null);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [saving, setSaving] = useState(false);
  const [validationStatus, setValidationStatus] = useState('NOT_VALIDATED');
  const [savingValidation, setSavingValidation] = useState(false);
  const [pinCoords, setPinCoords] = useState(null);
  const [mapsError, setMapsError] = useState(false);
  const [geocodedAddress, setGeocodedAddress] = useState(null);
  const [geocoding, setGeocoding] = useState(false);

  const dropdownRef    = useRef(null);
  const mapDivRef      = useRef(null);
  const gmapRef        = useRef(null);
  const gmarkerRef     = useRef(null);
  const geocodeTimerRef  = useRef(null);
  const manualMoveTimer  = useRef(null);

  // Debounced reverse geocode: llamada 600ms después del último movimiento del pin
  const triggerReverseGeocode = (lat, lng) => {
    clearTimeout(geocodeTimerRef.current);
    setGeocodedAddress(null);
    geocodeTimerRef.current = setTimeout(async () => {
      setGeocoding(true);
      try {
        const address = await apiService.reverseGeocode(lat, lng);
        setGeocodedAddress(address);
      } catch (e) {
        // silencioso — el preview simplemente no muestra dirección
      } finally {
        setGeocoding(false);
      }
    }, 600);
  };

  // Move the map + marker to a new position (imperative, no re-render needed)
  const moveMapTo = (lat, lng) => {
    if (!gmapRef.current || !gmarkerRef.current) return;
    const pos = { lat, lng };
    gmapRef.current.setCenter(pos);
    gmapRef.current.setZoom(17);
    gmarkerRef.current.setPosition(pos);
    gmarkerRef.current.setVisible(true);
    setPinCoords({ lat, lng });
    triggerReverseGeocode(lat, lng);
  };

  // Create the Google Map once the modal div is mounted
  useEffect(() => {
    if (!mapDivRef.current || gmapRef.current) return;

    if (!window.google || !window.google.maps) {
      setMapsError(true);
      return;
    }

    // Read pinCoords from the DOM-independent order prop to avoid stale closure
    const orderCoords = (order && order.latitude && order.longitude)
      ? { lat: parseFloat(order.latitude), lng: parseFloat(order.longitude) }
      : null;
    const initial = orderCoords || { lat: -12.0464, lng: -77.0428 }; // Lima fallback

    try {
      gmapRef.current = new window.google.maps.Map(mapDivRef.current, {
        center: initial,
        zoom: orderCoords ? 17 : 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      gmarkerRef.current = new window.google.maps.Marker({
        position: orderCoords || null,
        map: gmapRef.current,
        draggable: true,
        visible: !!orderCoords,
      });

      gmarkerRef.current.addListener('dragend', () => {
        const p = gmarkerRef.current.getPosition();
        const lat = p.lat(); const lng = p.lng();
        setSelected(null);
        setPinCoords({ lat, lng });
        setManualLat(lat.toFixed(6));
        setManualLng(lng.toFixed(6));
        triggerReverseGeocode(lat, lng);
      });

      gmapRef.current.addListener('click', (e) => {
        gmarkerRef.current.setPosition(e.latLng);
        gmarkerRef.current.setVisible(true);
        const lat = e.latLng.lat(); const lng = e.latLng.lng();
        setSelected(null);
        setPinCoords({ lat, lng });
        setManualLat(lat.toFixed(6));
        setManualLng(lng.toFixed(6));
        triggerReverseGeocode(lat, lng);
      });

      // Detect auth error inserted by Maps SDK into the div
      const observer = new MutationObserver(() => {
        if (mapDivRef.current && mapDivRef.current.querySelector('.gm-err-container')) {
          setMapsError(true);
          observer.disconnect();
        }
      });
      observer.observe(mapDivRef.current, { childList: true, subtree: true });
    } catch (e) {
      setMapsError(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (order) {
      const prefill = order.full_address || order.address || '';
      setSearchQuery(prefill);
      setSuggestions([]);
      setSelected(null);
      setManualLat('');
      setManualLng('');

      const existingCoords = (order.latitude && order.longitude)
        ? { lat: parseFloat(order.latitude), lng: parseFloat(order.longitude) }
        : null;
      setPinCoords(existingCoords);
      setGeocodedAddress(null);
      setValidationStatus(order.coords_validated || 'NOT_VALIDATED');
    }
    return () => {
      clearTimeout(geocodeTimerRef.current);
      clearTimeout(manualMoveTimer.current);
    };
  }, [order]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setSelected(null);
    setShowDropdown(false);
    setSuggestions([]);
  };

  const handleSearch = async () => {
    if (searchQuery.trim().length < 3) return;
    setSearchLoading(true);
    setShowDropdown(false);
    try {
      console.log('[Places] Buscando:', searchQuery);
      const results = await apiService.placesAutocomplete(searchQuery);
      console.log('[Places] Resultados:', results.length, results);
      setSuggestions(results);
      setShowDropdown(true); // mostrar siempre: resultados o "sin resultados"
      if (results.length === 0) {
        console.warn('[Places] Sin resultados para:', searchQuery);
      }
    } catch (err) {
      console.error('[Places] Error autocomplete:', err);
      toast.error('Error al buscar en Google Places. Revisa la conexión.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
  };

  const handleValidationChange = async (e) => {
    const value = e.target.value;
    setValidationStatus(value);
    setSavingValidation(true);
    try {
      await apiService.markAddressValidated(order.id_address, value);
      toast.success(value === 'VALIDATED' ? '✅ Dirección marcada como validada' : '⚠️ Dirección marcada como no validada');
    } catch (err) {
      toast.error('Error al actualizar estado de validación');
      setValidationStatus(validationStatus); // revert
    } finally {
      setSavingValidation(false);
    }
  };

  const handleSelectSuggestion = async (suggestion) => {
    setShowDropdown(false);
    setSearchQuery(suggestion.description);
    setSuggestions([]);
    setSearchLoading(true);

    try {
      const details = await apiService.placeDetails(suggestion.place_id);
      setSelected(details);
      setManualLat(String(details.latitude));
      setManualLng(String(details.longitude));
      moveMapTo(details.latitude, details.longitude);
      toast.success('📍 Ubicación encontrada');
    } catch (err) {
      toast.error('Error obteniendo detalles del lugar');
    } finally {
      setSearchLoading(false);
    }
  };

  const isManualValid = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  };

  const buildGoogleUrl = (lat, lng) =>
    `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  const handleSave = async () => {
    if (!pinCoords && !isManualValid()) return;

    setSaving(true);

    let latitude, longitude, url_google;

    if (pinCoords) {
      latitude  = pinCoords.lat;
      longitude = pinCoords.lng;
      // Si el pin viene de Places search, usar la URL con place_id (más precisa y con nombre)
      // Si el pin fue movido manualmente, generar desde coordenadas
      url_google = selected && selected.url_google
        ? selected.url_google
        : buildGoogleUrl(pinCoords.lat, pinCoords.lng);
    } else {
      latitude  = parseFloat(manualLat);
      longitude = parseFloat(manualLng);
      url_google = buildGoogleUrl(manualLat, manualLng);
    }

    const coords = { latitude, longitude, url_google };

    try {
      await apiService.updateAddressCoordinates(order.id_address, coords);

      toast.success('✏️ Coordenadas actualizadas');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(
        (error.response && error.response.data && error.response.data.error) ||
        'Error al actualizar coordenadas'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="modal-header">
          <h3>Editar Coordenadas GPS</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">

          {/* ── Columna de controles (derecha en desktop, primera en móvil) ── */}
          <div className="modal-controls-col">

            {/* Datos del pedido */}
            <div className="order-info-compact">
              <div className="order-info-compact-main">
                <strong>{order.customer_name}</strong>
                <span className="order-info-address">{order.full_address || order.address}</span>
                <span className="order-info-district">📍 {order.district}</span>
              </div>
              {order.leave_your_order && (
                <p className="delivery-instructions">
                  <strong>📝</strong> {order.leave_your_order}
                </p>
              )}
              <div className="validation-row">
                <label className="validation-label">Validación:</label>
                <select
                  className={`validation-select validation-select--${validationStatus === 'VALIDATED' ? 'valid' : 'invalid'}`}
                  value={validationStatus}
                  onChange={handleValidationChange}
                  disabled={savingValidation}
                >
                  <option value="NOT_VALIDATED">No validada</option>
                  <option value="VALIDATED">Validada</option>
                </select>
                {savingValidation && <span className="validation-saving">⏳</span>}
              </div>
            </div>

            {/* Buscador */}
            <div className="form-group controls-search" ref={dropdownRef}>
              <div className="places-search-wrapper">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Buscar dirección..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  disabled={saving}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="places-search-btn"
                  onClick={handleSearch}
                  disabled={saving || searchLoading || searchQuery.trim().length < 3}
                  title="Buscar en Google"
                >
                  {searchLoading ? '⏳' : '🔍'}
                </button>
              </div>
              {showDropdown && (
                <ul className="places-dropdown">
                  {suggestions.length > 0 ? suggestions.map((s) => (
                    <li
                      key={s.place_id}
                      className="places-dropdown-item"
                      onMouseDown={() => handleSelectSuggestion(s)}
                    >
                      📍 {s.description}
                    </li>
                  )) : (
                    <li className="places-dropdown-item places-dropdown-empty">
                      Sin resultados para "{searchQuery}"
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* Preview de coordenadas del pin */}
            {pinCoords && (
              <div className="preview-coords">
                <div className="preview-coords-info">
                  <strong>{selected ? 'Ubicación seleccionada' : 'Posición del pin'}</strong>
                  {selected
                    ? <span className="preview-address">{selected.formatted_address}</span>
                    : geocoding
                      ? <span className="preview-address preview-geocoding">⏳ Obteniendo dirección...</span>
                      : geocodedAddress && <span className="preview-address">{geocodedAddress}</span>
                  }
                  <span className="preview-latlng">
                    {pinCoords.lat.toFixed(6)}, {pinCoords.lng.toFixed(6)}
                  </span>
                </div>
                <a
                  href={selected && selected.url_google ? selected.url_google : `https://www.google.com/maps/search/?api=1&query=${pinCoords.lat},${pinCoords.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="preview-map-link"
                >
                  ↗
                </a>
              </div>
            )}

            {/* Coordenadas manuales */}
            {allowManual && (
              <>
                <div className="form-divider">— o ingresa manualmente —</div>
                <div className="form-row-manual">
                  <div className="form-group">
                    <label>Latitud</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="-12.123456"
                      value={manualLat}
                      onChange={(e) => {
                        const val = e.target.value;
                        setManualLat(val);
                        setSelected(null);
                        clearTimeout(manualMoveTimer.current);
                        manualMoveTimer.current = setTimeout(() => {
                          const lat = parseFloat(val);
                          const lng = parseFloat(manualLng);
                          if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                            moveMapTo(lat, lng);
                          }
                        }, 800);
                      }}
                      disabled={saving}
                    />
                  </div>
                  <div className="form-group">
                    <label>Longitud</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="-77.123456"
                      value={manualLng}
                      onChange={(e) => {
                        const val = e.target.value;
                        setManualLng(val);
                        setSelected(null);
                        clearTimeout(manualMoveTimer.current);
                        manualMoveTimer.current = setTimeout(() => {
                          const lat = parseFloat(manualLat);
                          const lng = parseFloat(val);
                          if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                            moveMapTo(lat, lng);
                          }
                        }, 800);
                      }}
                      disabled={saving}
                    />
                  </div>
                </div>
                {isManualValid() && !pinCoords && (
                  <div className="preview-coords">
                    <div className="preview-coords-info">
                      <strong>Coordenadas manuales</strong>
                      <span className="preview-latlng">{parseFloat(manualLat).toFixed(6)}, {parseFloat(manualLng).toFixed(6)}</span>
                    </div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${manualLat},${manualLng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="preview-map-link"
                    >
                      ↗
                    </a>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Columna del mapa (izquierda en desktop, segunda en móvil) ── */}
          <div className="modal-map-col">
            <div className="map-pin-container">
              {mapsError ? (
                <div className="maps-api-error">
                  <p>⚠️ <strong>Maps JavaScript API no habilitada.</strong></p>
                  <p>
                    Activa la API en{' '}
                    <a href="https://console.cloud.google.com/apis/library/maps-backend.googleapis.com" target="_blank" rel="noopener noreferrer">
                      Google Cloud Console
                    </a>
                    {' '}con la misma key del proyecto.
                  </p>
                  <p>Usa el buscador o ingresa las coordenadas manualmente.</p>
                </div>
              ) : (
                <>
                  <div ref={mapDivRef} className="map-div" />
                  <p className="map-pin-hint">📌 Haz clic o arrastra el marcador</p>
                </>
              )}
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || (!pinCoords && !isManualValid())}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditCoordinatesModal;
